import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getContentByCategory, getAllServices, SiteContent, Service } from '../services/contentService';

const FALLBACK_STEPS: SiteContent[] = [
  { id: 1, key: 'step-1', title: 'Consulta Inicial', content: 'Nos reunimos para conocer tus ideas, medidas y preferencias de estilo.', icon: '💬', category: 'step', sort_order: 1, is_active: true },
  { id: 2, key: 'step-2', title: 'Diseño y Materiales', content: 'Seleccionamos telas y diseñamos el patrón exclusivo para ti.', icon: '✏️', category: 'step', sort_order: 2, is_active: true },
  { id: 3, key: 'step-3', title: 'Confección', content: 'Elaboramos tu prenda con precisión y cuidado artesanal.', icon: '🪡', category: 'step', sort_order: 3, is_active: true },
  { id: 4, key: 'step-4', title: 'Entrega', content: 'Prueba final y ajustes si son necesarios. ¡Tu prenda lista!', icon: '✨', category: 'step', sort_order: 4, is_active: true },
];

const FALLBACK_CUSTOM_SERVICES: Service[] = [
  { id: 10, name: 'Vestidos de todo tipo y ocasión', description: '', price: 'Variable', icon: '👗', sort_order: 10, is_active: true },
  { id: 11, name: 'Trajes de baile folklórico', description: '', price: 'Variable', icon: '💃', sort_order: 11, is_active: true },
  { id: 12, name: 'Ropa para el hogar', description: '', price: 'Variable', icon: '🏠', sort_order: 12, is_active: true },
  { id: 13, name: 'Uniformes empresariales', description: '', price: 'Variable', icon: '👔', sort_order: 13, is_active: true },
  { id: 14, name: 'Accesorios textiles', description: '', price: 'Variable', icon: '🎀', sort_order: 14, is_active: true },
];

const PersonalizadoPage: React.FC = () => {
  const [steps, setSteps] = useState<SiteContent[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [stepsData, servicesData] = await Promise.all([
          getContentByCategory('step'),
          getAllServices()
        ]);
        setSteps(stepsData);
        // Filter to get only custom services
        const customServices = servicesData.filter(s => 
          s.metadata && s.metadata.includes('"type":"custom"')
        );
        setServices(customServices);
      } catch (error) {
        console.error('Error loading personalized page data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const displaySteps = steps.length > 0 ? steps : FALLBACK_STEPS;
  const displayServices = services.length > 0 ? services : FALLBACK_CUSTOM_SERVICES;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Page Header */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-white to-purple-50/50" />
        <div className="absolute top-10 left-1/3 w-48 h-48 bg-pink-400/10 rounded-full blur-[80px]" />
        <div className="absolute bottom-10 right-1/3 w-64 h-64 bg-purple-400/10 rounded-full blur-[80px]" />
        
        <div className="container-custom relative text-center">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-pink-500 mb-3 animate-fade-in">A tu medida</h3>
          <h1 className="text-4xl md:text-5xl font-bold font-display text-gray-900 mb-4 animate-slide-up">
            Servicio Personalizado
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto animate-slide-up-delayed">
            Prendas únicas diseñadas exclusivamente para ti, con atención al detalle en cada puntada
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16">
          <div className="container-custom">
            <div className="text-center mb-14">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-pink-500 mb-3">Proceso</h3>
              <h2 className="text-3xl md:text-4xl font-bold font-display text-gray-900">
                ¿Cómo funciona?
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              {displaySteps.map((step, idx) => {
                // Parse metadata to get step number
                let stepNumber = '0' + (idx + 1);
                try {
                  const metadata = step.metadata ? JSON.parse(step.metadata) : {};
                  if (metadata.number) stepNumber = metadata.number;
                } catch {}

                return (
                  <div key={step.id} className="group relative">
                    {/* Connector line */}
                    {idx < displaySteps.length - 1 && (
                      <div className="hidden lg:block absolute top-10 left-[calc(50%+50px)] w-[calc(100%-60px)] h-px bg-gradient-to-r from-pink-200 to-pink-100" />
                    )}
                    
                    <div className="relative bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 p-7 text-center hover:shadow-xl hover:shadow-pink-500/5 hover:-translate-y-2 transition-all duration-500 glow-hover">
                      <div className="absolute -top-3 -right-3 w-8 h-8 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center shadow-lg shadow-pink-500/30">
                        <span className="text-white text-xs font-bold">{stepNumber}</span>
                      </div>
                      <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300">
                        {step.icon || '✨'}
                      </div>
                      <h3 className="font-bold text-lg text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{step.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

      {/* Available Services */}
      <section className="py-16 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-50/30 to-transparent" />
          <div className="container-custom relative">
            <div className="text-center mb-14">
              <h3 className="text-sm font-semibold uppercase tracking-widest text-pink-500 mb-3">Servicios</h3>
              <h2 className="text-3xl md:text-4xl font-bold font-display text-gray-900">
                Lo que podemos crear para ti
              </h2>
            </div>
            
            <div className="max-w-3xl mx-auto space-y-4 stagger-children">
              {displayServices.map((service) => (
                <div key={service.id} className="group bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 px-7 py-5 flex items-center space-x-5 hover:shadow-lg hover:shadow-pink-500/5 hover:border-pink-200/30 hover:-translate-y-0.5 transition-all duration-500">
                  <span className="text-3xl group-hover:scale-110 transition-transform duration-300">
                    {service.icon || '🎨'}
                  </span>
                  <span className="text-lg font-medium text-gray-800">{service.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

      {/* CTA */}
      <section className="py-16 pb-20">
        <div className="container-custom">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pink-500 via-rose-500 to-purple-600 p-12 md:p-16 text-center">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl" />
            
            <div className="relative max-w-2xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-display">
                ¿Lista para tu prenda única?
              </h2>
              <p className="text-white/90 text-lg mb-8">
                Agenda una consulta gratuita y comencemos a diseñar juntos
              </p>
              <Link
                to="/contacto"
                className="inline-flex items-center px-8 py-4 bg-white text-pink-600 rounded-2xl font-semibold shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all duration-300"
              >
                Agendar Consulta
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PersonalizadoPage;